import { GetApp } from '@mui/icons-material';
import {
  Divider,
  FormControlLabel,
  FormHelperText,
  RadioGroup
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AutocompleteComponent from 'components/design-system/Autocomplete/Autocomplete';
import ButtonComponent from 'components/design-system/Button/Button';
import { DatePickerComponent } from 'components/design-system/DatePicker/DatePicker';
import Heading from 'components/design-system/Heading/Heading';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import RadioComponent from 'components/design-system/Radio/Radio';
import Text from 'components/design-system/Text/Text';
import { PERMISOS } from 'const/Permisos';
import { AuthContext } from 'context';
import { es } from 'date-fns/locale';
import { useContext, useEffect } from 'react';
import { DATO_A_EXPORTAR, INIT_DATA, TIPO_REPORTE } from './helpers/Texts';
import { format } from 'date-fns';

export const DescargarReportesCampos = ({
  handleChange,
  values,
  errors,
  touched,
  desplegarComponente,
  setValues,
  setFieldValue,
  grupos,
  negocios,
  sucursales,
  recargarSucursales,
  isSubmitting,
  recargarNegocios
}) => {
  const { isTienePermisos } = useContext(AuthContext);

  useEffect(() => {
    //LIMPIO LOS DATOS DE CAMPOS AL CAMBIAR EL TIPO DE REPORTE A GENERAR
    setValues({
      ...INIT_DATA,
      tipo: values.tipo
    });
  }, [values.tipo]);

  useEffect(() => {
    if (values.negocios) {
      recargarSucursales(values.negocios);
    }
  }, [values.negocios]);

  useEffect(() => {
    if (values.grupo) {
      recargarNegocios(values.grupo);
    }
  }, [values.grupo]);

  return (
    <div className="grid grid-cols-12 p-5 gap-5">
      <div className="col-span-12">
        <RadioGroup
          aria-label="tipo"
          name="tipo"
          value={values.tipo}
          onChange={handleChange}
        >
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12">
              <Text>Selecciona el tipo de reporte a generar:</Text>
            </div>

            {isTienePermisos(PERMISOS.reportePersonalizado) && (
              <div className="col-span-12 md:col-span-6 pr-10">
                <FormControlLabel
                  className="px-2"
                  value="personalizado"
                  control={<RadioComponent />}
                  label={
                    <Heading type="h4" className="text-uv-primary-0 mb-1">
                      Personalizado
                    </Heading>
                  }
                />
                <Text className="pl-10">
                  Podrás seleccionar manualmente el nivel de los registros de
                  los cuales quieres obtener el reporte, grupo, negocio o
                  sucursal
                </Text>
              </div>
            )}

            {isTienePermisos(PERMISOS.reporteCompleto) && (
              <div className="col-span-12 md:col-span-6 pr-10">
                <div>
                  <FormControlLabel
                    className="px-2"
                    value="completo"
                    control={<RadioComponent />}
                    label={
                      <Heading type="h4" className="text-uv-primary-0 mb-1">
                        Completo
                      </Heading>
                    }
                  />
                  <Text className="pl-10">
                    Se exportarán todas los negocios registrados en sistema
                    junto con todas sus sucursales.
                  </Text>
                </div>
              </div>
            )}

            {isTienePermisos(PERMISOS.reporteSINADER) && (
              <div className="col-span-12 md:col-span-6 pr-10">
                <FormControlLabel
                  className="px-2"
                  value="sinader"
                  control={<RadioComponent />}
                  label={
                    <Heading type="h4" className="text-uv-primary-0 mb-1">
                      SINADER
                    </Heading>
                  }
                />
                <Text className="pl-10">
                  Se exportarán las transacciones finalizadas de residuo-recurso
                  del <strong>Marketplace</strong> y residuos declarados en{' '}
                  <strong>Gestiona</strong>, en formato apto para la opción
                  "carga masiva de residuos", de las declaraciones mensuales en
                  la plataforma SINADER.
                </Text>
              </div>
            )}
          </div>
        </RadioGroup>
      </div>
      <div className="col-span-12 my-4">
        <Divider />
      </div>

      {desplegarComponente(values.tipo, null, 'datoExportar') && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          <AutocompleteComponent
            autoComplete={false}
            disableClearable={false}
            onKeyPress={e => {
              e.preventDefault();
            }}
            clearIcon={null}
            name="tipoExportacion"
            options={[
              DATO_A_EXPORTAR.USUARIO,
              DATO_A_EXPORTAR.RESIDUO,
              ...(values.tipo === TIPO_REPORTE.PERSONALIZADO
                ? [DATO_A_EXPORTAR.INDICADOR]
                : []),
              DATO_A_EXPORTAR.RESIDUO_DECLARADO,
              DATO_A_EXPORTAR.RECURSO_ADQUIRIDO
            ]}
            onChange={ev => {
              const target = {
                name: 'tipoExportacion',
                value: ev.target.innerText
              };
              handleChange({ target });
              setFieldValue('sucursales', []);

              if (ev.target.innerText === 'Indicador') {
                setFieldValue('formato', 'pdf');
              } else {
                setFieldValue('formato', 'xlsx');
              }
            }}
            value={values.tipoExportacion || ''}
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
          >
            <InputComponent
              fullWidth
              label={<LabelComponent>Datos a exportar</LabelComponent>}
            />
          </AutocompleteComponent>
        </div>
      )}

      {desplegarComponente(values.tipo, values.tipoExportacion, 'formato') && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          <LabelComponent>Formato de Archivo</LabelComponent>
          <RadioGroup
            row
            name="formato"
            defaultValue="xlsx"
            value={values.formato || ''}
            onChange={handleChange}
          >
            {values.tipoExportacion === 'Indicador' ? (
              <FormControlLabel
                value="pdf"
                control={<RadioComponent />}
                label={<LabelComponent>PDF</LabelComponent>}
                disabled={values.tipoExportacion === 'Indicador'}
              />
            ) : (
              <>
                <FormControlLabel
                  value="xlsx"
                  control={<RadioComponent />}
                  label={<LabelComponent>XLSX</LabelComponent>}
                />
                <FormControlLabel
                  value="csv"
                  control={<RadioComponent />}
                  label={<LabelComponent>CSV</LabelComponent>}
                />
              </>
            )}
          </RadioGroup>
          {errors.formato ? (
            <FormHelperText>{errors.formato}</FormHelperText>
          ) : null}
        </div>
      )}

      {desplegarComponente(values.tipo, values.tipoExportacion, 'periodo') && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          <LabelComponent>Período</LabelComponent>
          <RadioGroup
            row
            aria-label="position"
            name="periodo"
            value={values.periodo}
            onChange={handleChange}
          >
            <FormControlLabel
              value="anual"
              className="mr-5"
              control={<RadioComponent />}
              label={<LabelComponent>Anual</LabelComponent>}
            />
            <FormControlLabel
              value="mensual"
              control={<RadioComponent />}
              label={<LabelComponent>Mensual</LabelComponent>}
            />
          </RadioGroup>
        </div>
      )}

      {desplegarComponente(
        values.tipo,
        values.tipoExportacion,
        'calendario'
      ) && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          {values.periodo === 'anual' && (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={es}
            >
              <DesktopDatePicker
                onChange={value => {
                  handleChange({
                    target: {
                      name: 'anyo',
                      value: value
                    }
                  });
                }}
                views={['year']}
                name="anyo"
                label={<LabelComponent>Seleccionar año</LabelComponent>}
                defaultValue={values.anyo}
                value={values.anyo}
                slots={{ textField: DatePickerComponent }}
              />
              {errors.anyo ? (
                <Text className="text-error-dark text-xs mt-0.5">
                  {errors.anyo}
                </Text>
              ) : null}
            </LocalizationProvider>
          )}

          {values.periodo === 'mensual' && (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={es}
            >
              <DesktopDatePicker
                onChange={value => {
                  handleChange({
                    target: {
                      name: 'mesAnyo',
                      value: value
                    }
                  });
                }}
                views={['year', 'month']}
                format="MM/yyyy"
                name="mesAnyo"
                label={<LabelComponent>Seleccionar mes</LabelComponent>}
                defaultValue={values.mesAnyo}
                value={values.mesAnyo}
                slots={{ textField: DatePickerComponent }}
              />
              {errors.mesAnyo ? (
                <Text className="text-error-dark text-xs mt-0.5">
                  {errors.mesAnyo}
                </Text>
              ) : null}
            </LocalizationProvider>
          )}
        </div>
      )}

      {desplegarComponente(values.tipo, values.tipoExportacion, 'fechas') && (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
            <DesktopDatePicker
              onChange={value => {
                setFieldValue(
                  'fechaDisponibilidadInicio_str',
                  format(value, 'yyyy-MM-dd')
                );
                handleChange({
                  target: {
                    name: 'fechaDisponibilidadInicio',
                    value: value
                  }
                });
              }}
              label={<LabelComponent>Fecha inicio</LabelComponent>}
              inputFormat="dd/MM/yyyy"
              defaultValue={values.fechaDisponibilidadInicio}
              value={values.fechaDisponibilidadInicio}
              maxDate={values.fechaDisponibilidadTermino}
              slots={{ textField: DatePickerComponent }}
            />
            {errors.fechaDisponibilidadInicio ? (
              <Text className="text-error-dark text-xs mt-0.5">
                {errors.fechaDisponibilidadInicio}
              </Text>
            ) : null}
          </div>

          <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
            <DesktopDatePicker
              onChange={value => {
                setFieldValue(
                  'fechaDisponibilidadTermino_str',
                  format(value, 'yyyy-MM-dd')
                );
                handleChange({
                  target: {
                    name: 'fechaDisponibilidadTermino',
                    value: value
                  }
                });
              }}
              label={<LabelComponent>Fecha término</LabelComponent>}
              inputFormat="dd/MM/yyyy"
              minDate={values.fechaDisponibilidadInicio}
              defaultValue={values.fechaDisponibilidadTermino}
              value={values.fechaDisponibilidadTermino}
              slots={{ textField: DatePickerComponent }}
            />
            {errors.fechaDisponibilidadTermino ? (
              <Text className="text-error-dark text-xs mt-0.5">
                {errors.fechaDisponibilidadTermino}
              </Text>
            ) : null}
          </div>
        </LocalizationProvider>
      )}

      {desplegarComponente(values.tipo, values.tipoExportacion, 'grupo') && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          <AutocompleteComponent
            name="grupo"
            accesor="nombreGrupo"
            className="w-full "
            options={grupos}
            onChange={(ev, grupo, reason) => {
              setFieldValue('negocios', []);
              setFieldValue('sucursales', []);

              if (reason === 'clear') {
                setFieldValue('grupo', []);
                return;
              }
              handleChange({
                target: {
                  name: 'grupo',
                  value: grupo
                }
              });
            }}
            value={values.grupo}
            getOptionLabel={option => {
              return option.nombreGrupo || '';
            }}
          >
            <InputComponent
              fullWidth
              className="h-9"
              label={<LabelComponent>Selecciona un grupo</LabelComponent>}
              touched={touched.grupo ? true : false}
              estado={
                errors.grupo
                  ? {
                      tipo: 'error',
                      mensaje: errors.grupo
                    }
                  : null
              }
            />
          </AutocompleteComponent>
        </div>
      )}

      {desplegarComponente(values.tipo, values.tipoExportacion, 'negocios') && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          <AutocompleteComponent
            key={values.tipoExportacion}
            multiple={values.tipoExportacion === 'Indicador' ? false : true}
            options={negocios || []}
            name="negocios"
            accesor="nombreEmpresa"
            disabled={!negocios || negocios.length === 0}
            onChange={(ev, negocios, reason) => {
              if (reason === 'clear') {
                setFieldValue('negocios', []);
                setFieldValue('sucursales', []);
                return;
              }

              const empresaAux = Array.isArray(negocios)
                ? negocios
                : [negocios];

              handleChange({
                target: {
                  name: 'negocios',
                  value: empresaAux
                }
              });
              setFieldValue('sucursales', []);
            }}
            value={values.negocios}
            getOptionLabel={option => (option ? option.nombreEmpresa : '')}
          >
            <InputComponent
              fullWidth
              label={
                <LabelComponent>Seleccionar el/lo(s) negocio(s)</LabelComponent>
              }
            />
          </AutocompleteComponent>
        </div>
      )}

      {desplegarComponente(
        values.tipo,
        values.tipoExportacion,
        'sucursales'
      ) && (
        <div className="col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-3">
          <AutocompleteComponent
            multiple
            key={`sucursales-${values.tipoExportacion}`}
            name="sucursales"
            options={sucursales || []}
            accesor="nombreSucursal"
            onChange={(ev, sucursales, reason) => {
              if (reason === 'clear') {
                setFieldValue('sucursales', []);
                return;
              }
              setFieldValue('sucursales', sucursales);
            }}
            value={values.sucursales}
            getOptionLabel={option => (option ? option.nombreSucursal : '')}
          >
            <InputComponent
              fullWidth
              label={
                <LabelComponent>Seleccionar la(s) sucursal(es)</LabelComponent>
              }
              touched={touched.sucursales ? true : false}
              estado={
                errors.sucursales
                  ? {
                      tipo: 'error',
                      mensaje: errors.sucursales
                    }
                  : null
              }
            />
          </AutocompleteComponent>
        </div>
      )}

      <div className="col-span-12">
        <Divider />
      </div>
      <div className="col-span-12">
        <ButtonComponent
          form="descargarReporte"
          buttonType="submit"
          className="h-auto"
          disabled={isSubmitting}
        >
          Descargar
          <GetApp className="ml-1" />
        </ButtonComponent>
      </div>
    </div>
  );
};
